import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiUsers, FiLoader } from 'react-icons/fi';
import { contatosAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { ContatoCard, ContatosFiltros, ContatosPaginacao } from '../components/contatos';

const DEFAULT_ITEMS_PER_PAGE = 50;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const Contatos = () => {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    id_cliente: '',
    page: 1,
    limit: DEFAULT_ITEMS_PER_PAGE
  });

  // Carregar clientes e aplicar filtro automático se necessário
  useEffect(() => {
    const carregarClientesEAplicarFiltro = async () => {
      try {
        setLoadingClientes(true);
        const response = await contatosAPI.listarClientes();
        if (response.data.success) {
          const clientesCarregados = response.data.data || [];
          setClientes(clientesCarregados);

          let filtrosParaBusca = filtros;
          if (clientesCarregados.length === 1 && clientesCarregados[0]?.id != null) {
            const clienteId = clientesCarregados[0].id.toString();
            filtrosParaBusca = {
              ...filtros,
              id_cliente: clienteId
            };
            setFiltros(filtrosParaBusca);
          }

          carregarContatos(filtrosParaBusca);
        } else {
          carregarContatos(filtros);
        }
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
        carregarContatos(filtros);
      } finally {
        setLoadingClientes(false);
      }
    };

    carregarClientesEAplicarFiltro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizarNumero = (valor, fallback) => {
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : fallback;
  };

  const normalizarPagination = (dadosPaginacao = {}, fallback = {}) => {
    const fallbackCurrentPage = fallback.currentPage ?? 1;
    const fallbackItemsPerPage = fallback.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE;
    const fallbackTotalItems = fallback.totalItems ?? 0;
    const fallbackHasNext =
      typeof fallback.hasNextPage === 'boolean'
        ? fallback.hasNextPage
        : fallbackCurrentPage < (fallback.totalPages ?? 1);
    const fallbackHasPrev =
      typeof fallback.hasPrevPage === 'boolean'
        ? fallback.hasPrevPage
        : fallbackCurrentPage > 1;

    const itemsPerPage = normalizarNumero(
      dadosPaginacao.itemsPerPage,
      fallbackItemsPerPage
    );
    const totalItems = normalizarNumero(
      dadosPaginacao.totalItems,
      fallbackTotalItems
    );
    const currentPage = normalizarNumero(
      dadosPaginacao.currentPage,
      fallbackCurrentPage
    );
    const totalPagesCalculado =
      itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
    const totalPages = normalizarNumero(
      dadosPaginacao.totalPages,
      fallback.totalPages ?? totalPagesCalculado
    );

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      hasNextPage:
        typeof dadosPaginacao.hasNextPage === 'boolean'
          ? dadosPaginacao.hasNextPage
          : fallbackHasNext ?? currentPage < totalPages,
      hasPrevPage:
        typeof dadosPaginacao.hasPrevPage === 'boolean'
          ? dadosPaginacao.hasPrevPage
          : fallbackHasPrev ?? currentPage > 1
    };
  };

  // Carregar contatos
  const carregarContatos = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await contatosAPI.listar(params);
      
      if (response.data.success) {
        const contatosRecebidos = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        const contatosOrdenados = [...contatosRecebidos].sort((contatoA, contatoB) => {
          const nomeA = (contatoA.nome || contatoA.session_key || '').toString().toLowerCase();
          const nomeB = (contatoB.nome || contatoB.session_key || '').toString().toLowerCase();
          return nomeA.localeCompare(nomeB, 'pt-BR');
        });

        const paginaSolicitada = normalizarNumero(
          params?.page ?? filtros.page ?? pagination.currentPage ?? 1,
          1
        );
        const limiteSolicitado = normalizarNumero(
          params?.limit ?? filtros.limit ?? pagination.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE,
          DEFAULT_ITEMS_PER_PAGE
        );

        const precisaForcarPaginacao = contatosOrdenados.length > limiteSolicitado;
        const inicioPagina = Math.max(0, (paginaSolicitada - 1) * limiteSolicitado);
        const fimPagina = inicioPagina + limiteSolicitado;
        const contatosPaginados = precisaForcarPaginacao
          ? contatosOrdenados.slice(inicioPagina, fimPagina)
          : contatosOrdenados;

        setContatos(contatosPaginados);
        setPagination(prevPagination =>
          normalizarPagination(response.data.pagination, {
            ...prevPagination,
            currentPage: paginaSolicitada,
            itemsPerPage: limiteSolicitado,
            totalItems:
              response.data.pagination?.totalItems ??
              prevPagination.totalItems ??
              contatosRecebidos.length,
            totalPages:
              response.data.pagination?.totalPages ??
              prevPagination.totalPages ??
              Math.ceil(
                (response.data.pagination?.totalItems ?? contatosRecebidos.length) /
                  (response.data.pagination?.itemsPerPage ?? limiteSolicitado)
              ),
            hasNextPage: response.data.pagination?.hasNextPage,
            hasPrevPage: response.data.pagination?.hasPrevPage
          })
        );
      } else {
        setError('Erro ao carregar contatos');
      }
    } catch (err) {
      console.error('Erro ao carregar contatos:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    setFiltros(prevFiltros => ({
      ...novosFiltros,
      page: novosFiltros.page !== undefined ? novosFiltros.page : prevFiltros.page,
      limit: novosFiltros.limit !== undefined ? novosFiltros.limit : (prevFiltros.limit || DEFAULT_ITEMS_PER_PAGE)
    }));
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarContatos({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarContatos(filtrosParaBusca);
  };

  // Buscar com filtros específicos e atualizar estado
  const handleBuscarWithFilters = (filters) => {
    setFiltros(filters);
    carregarContatos(filters);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarContatos(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1 // Reset para primeira página quando mudar o limite
    };
    setFiltros(novosFiltros);
    carregarContatos(novosFiltros);
  };

  // Verificar se deve mostrar filtro de cliente
  const shouldShowClienteFilter = () => {
    // Se ainda está carregando clientes, não mostrar
    if (loadingClientes) return false;
    
    // Se for admin, sempre mostrar (mesmo que tenha apenas 1 cliente)
    // Se não for admin, mostrar apenas se tiver mais de 1 cliente
    return clientes.length > 1;
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando contatos...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar contatos</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarContatos(filtros)}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (contatos.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiUsers />
          </EmptyIcon>
          <EmptyTitle>Nenhum contato encontrado</EmptyTitle>
          <EmptyText>
            Não foram encontrados contatos com os filtros aplicados.
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {contatos.map((contato) => (
            <ContatoCard 
              key={contato.session_key} 
              contato={contato}
              onFavoritoChange={(sessionKey, novoFavorito) => {
                // Atualizar o estado local do contato quando favorito mudar
                setContatos(prevContatos => 
                  prevContatos.map(c => 
                    c.session_key === sessionKey 
                      ? { ...c, favorito: novoFavorito }
                      : c
                  )
                );
              }}
            />
          ))}
        </CardsGrid>

        {/* Paginação - sempre mostrar para permitir mudar registros por página */}
        {!loading && (
          <ContatosPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Contatos">
      <Container>
        <Content>
          {!loadingClientes && (
            <ContatosFiltros
              filtros={filtros}
              onFiltrosChange={handleFiltrosChange}
              onBuscar={handleBuscar}
              onBuscarWithFilters={handleBuscarWithFilters}
              loading={loading}
              showClienteFilter={shouldShowClienteFilter()}
              clientes={clientes}
            />
          )}

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default Contatos;

